import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, IconButton, LinearProgress, MenuItem, TextField, Tooltip } from '@mui/material';
import { EditRounded, InfoOutlined } from '@mui/icons-material';
import { useAppDispatch } from '../../store';
import { addMessage } from '../../store/localApp';
import { uploadFile } from '../../helpers/upload';
import { useAppTranslation } from '../../services/i18n';
import { SettingInfoButton, SettingsCard } from './SettingRow';
import { SettingsTabProps } from './settingsTab';

const LANGUAGES = ['cs', 'en', 'pl'];
const LOGO_TYPES = ['image/jpeg', 'image/png'];
const LOGO_MAX_SIZE = 10 * 1024 * 1024;

/** Where the members reach the organization; app_url carries a custom domain when there is one */
export const organizationHost = (settings: SettingsTabProps['settings']) => {
  try {
    return new URL(settings.app_url).host;
  } catch (e) {
    return `${settings.subdomain}.knowspread.com`;
  }
};

export const OrganizationSettings = ({ settings, isOwner, onSave }: SettingsTabProps) => {
  const t = useAppTranslation();
  const dispatch = useAppDispatch();
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(settings.name);
  const [language, setLanguage] = useState(settings.language);
  const [isUploading, setIsUploading] = useState(false);
  const [brokenLogoUrl, setBrokenLogoUrl] = useState<string>();

  useEffect(() => setName(settings.name), [settings.name]);
  useEffect(() => setLanguage(settings.language), [settings.language]);

  const trimmedName = name.trim();
  const isNameDirty = trimmedName !== settings.name;
  const logoUrl = settings.small_logo_picture_url || settings.logo_picture_url;
  const address = organizationHost(settings);

  const saveName = (event: React.FormEvent) => {
    event.preventDefault();
    if (trimmedName && isNameDirty) {
      onSave({ name: trimmedName }, 'companySettings.organization.nameSaved');
    }
  };

  const saveLanguage = (event: React.FormEvent) => {
    event.preventDefault();
    onSave({ language }, 'companySettings.organization.languageSaved');
  };

  const handleLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !LOGO_TYPES.includes(file.type)) {
      return;
    }
    if (file.size > LOGO_MAX_SIZE) {
      dispatch(
        addMessage({ severity: 'error', title: t('companySettings.organization.logoTooBig') })
      );
      return;
    }
    setIsUploading(true);
    try {
      const uploaded = await uploadFile(dispatch, file, true);
      if (uploaded) {
        await onSave({ file_id: uploaded.id }, 'companySettings.organization.logoSaved');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const avatar =
    logoUrl && logoUrl !== brokenLogoUrl ? (
      <img
        className={'organization-logo__image'}
        src={logoUrl}
        alt={''}
        onError={() => setBrokenLogoUrl(logoUrl)}
      />
    ) : (
      <span className={'organization-logo__initial'}>{settings.name.charAt(0).toUpperCase()}</span>
    );

  return (
    <>
      <SettingsCard title={t('companySettings.organization.identityTitle')}>
        <div className={'organization-identity'}>
          <div className={'organization-logo'}>
            {isOwner ? (
              <button
                type={'button'}
                className={'organization-logo__button'}
                disabled={isUploading}
                aria-label={t('companySettings.organization.logoChange')}
                title={t('companySettings.organization.logoChange')}
                onClick={() => fileInput.current?.click()}
              >
                {avatar}
                <span className={'organization-logo__edit'}>
                  <EditRounded fontSize={'small'} />
                </span>
              </button>
            ) : (
              avatar
            )}
            <input
              ref={fileInput}
              type={'file'}
              accept={LOGO_TYPES.join(', ')}
              hidden
              onChange={handleLogo}
            />
            {isUploading && <LinearProgress className={'organization-logo__progress'} />}
          </div>
          <div className={'organization-identity__fields'}>
            <form className={'organization-identity__form'} onSubmit={saveName} noValidate>
              <TextField
                fullWidth
                required
                variant={'standard'}
                label={t('companySettings.organization.name')}
                value={name}
                disabled={!isOwner}
                error={!trimmedName}
                helperText={
                  !trimmedName
                    ? t('companySettings.organization.nameRequired')
                    : !isOwner
                    ? t('companySettings.ownerOnly')
                    : undefined
                }
                onChange={(event) => setName(event.target.value)}
              />
              {isNameDirty && trimmedName && (
                <Button type={'submit'} color={'primary'}>
                  {t('companySettings.save')}
                </Button>
              )}
            </form>
            <div className={'organization-identity__subdomain'}>
              <TextField
                fullWidth
                disabled
                required
                variant={'standard'}
                label={t('companySettings.organization.subdomain')}
                value={settings.subdomain}
                helperText={t('companySettings.organization.address', { address })}
              />
              <SettingInfoButton
                info={{
                  title: t('companySettings.organization.subdomainInfoTitle'),
                  text: t('companySettings.organization.subdomainInfoText'),
                }}
              />
            </div>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title={t('companySettings.organization.localizationTitle')}>
        <form onSubmit={saveLanguage}>
          <div className={'organization-language__label'}>
            <span id={'organization-language-label'}>
              {t('companySettings.organization.language.label')} *
            </span>
            <Tooltip enterTouchDelay={0} title={t('companySettings.organization.language.info')}>
              <IconButton
                size={'small'}
                className={'setting-row__info'}
                aria-label={t('companySettings.organization.language.info')}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </div>
          <div className={'organization-identity__form'}>
            <TextField
              select
              fullWidth
              variant={'standard'}
              slotProps={{ select: { labelId: 'organization-language-label' } }}
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
            >
              {LANGUAGES.map((code) => (
                <MenuItem key={code} value={code}>
                  {t(`companySettings.organization.language.options.${code}`)}
                </MenuItem>
              ))}
            </TextField>
            {language !== settings.language && (
              <Button type={'submit'} color={'primary'}>
                {t('companySettings.save')}
              </Button>
            )}
          </div>
        </form>
      </SettingsCard>
    </>
  );
};
